/* =========================================================================
   Setup SQL → jadval strukturasi.

   Backend namuna ma'lumotlarni faqat xom `setupSql` ko'rinishida qaytaradi
   (CREATE TABLE + INSERT INTO). Foydalanuvchiga SQL emas, haqiqiy jadval
   ko'rsatish uchun shu yerda parse qilinadi.

   Bu to'liq SQL parser EMAS — masala setup'lari uchun yetarli bo'lgan tor
   qism: CREATE TABLE, INSERT INTO ... VALUES. Tanimagan narsasini jimgina
   tashlab ketadi; hech narsa chiqmasa chaqiruvchi xom SQL ga qaytadi.
   ========================================================================= */

export type CellValue = string | number | null;

export interface ParsedColumn {
  name: string;
  type: string;
}

export interface ParsedTable {
  name: string;
  columns: ParsedColumn[];
  rows: CellValue[][];
}

/** Jadval darajasidagi cheklovlar — ustun emas, o'tkazib yuboriladi. */
const TABLE_CONSTRAINTS = /^(PRIMARY|FOREIGN|UNIQUE|CHECK|CONSTRAINT|KEY|INDEX)\b/i;

const CREATE_TABLE_RE = /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?["`[]?(\w+)["`\]]?\s*\(/gi;
const INSERT_INTO_RE = /INSERT\s+INTO\s+["`[]?(\w+)["`\]]?\s*(\(([^)]*)\))?\s*VALUES\s*/gi;

/**
 * Vergul (yoki boshqa ajratgich) bo'yicha bo'ladi, lekin qavs ichidagi va
 * qo'shtirnoq ichidagi ajratgichlarga tegmaydi — VARCHAR(50) va 'a,b' uchun.
 */
function splitTopLevel(input: string, delim = ','): string[] {
  const out: string[] = [];
  let depth = 0;
  let inStr = false;
  let cur = '';

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (inStr) {
      cur += ch;
      if (ch === "'") {
        if (input[i + 1] === "'") {
          cur += "'"; // SQL da '' — qochirilgan qo'shtirnoq
          i++;
        } else {
          inStr = false;
        }
      }
      continue;
    }

    if (ch === "'") {
      inStr = true;
      cur += ch;
    } else if (ch === '(') {
      depth++;
      cur += ch;
    } else if (ch === ')') {
      depth--;
      cur += ch;
    } else if (ch === delim && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }

  if (cur.trim()) out.push(cur.trim());
  return out;
}

/** `openIndex` dagi '(' ga mos yopuvchi ')' indeksi; topilmasa -1. */
function findClosingParen(s: string, openIndex: number): number {
  let depth = 0;
  let inStr = false;

  for (let i = openIndex; i < s.length; i++) {
    const ch = s[i];

    if (inStr) {
      if (ch === "'") {
        if (s[i + 1] === "'") i++;
        else inStr = false;
      }
      continue;
    }

    if (ch === "'") inStr = true;
    else if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** `start` dan boshlab qavs/qo'shtirnoqdan tashqaridagi birinchi ';' indeksi. */
function findStatementEnd(s: string, start: number): number {
  let depth = 0;
  let inStr = false;

  for (let i = start; i < s.length; i++) {
    const ch = s[i];

    if (inStr) {
      if (ch === "'") {
        if (s[i + 1] === "'") i++;
        else inStr = false;
      }
      continue;
    }

    if (ch === "'") inStr = true;
    else if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ';' && depth === 0) return i;
  }
  return s.length;
}

/** `id INT PRIMARY KEY` → {name: 'id', type: 'INT'}; `name VARCHAR(50)` → VARCHAR(50) */
function parseColumnDef(def: string): ParsedColumn | null {
  if (TABLE_CONSTRAINTS.test(def)) return null;

  const m = def.match(/^["`[]?(\w+)["`\]]?\s+(\w+(?:\s*\([^)]*\))?)/);
  if (!m) return null;

  return { name: m[1], type: m[2].replace(/\s+/g, '').toUpperCase() };
}

function parseValue(raw: string): CellValue {
  const v = raw.trim();

  if (/^NULL$/i.test(v)) return null;

  if (v.startsWith("'") && v.endsWith("'") && v.length >= 2) {
    return v.slice(1, -1).replace(/''/g, "'");
  }

  if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);

  if (/^TRUE$/i.test(v)) return 'true';
  if (/^FALSE$/i.test(v)) return 'false';

  return v;
}

/**
 * Setup SQL dan jadvallarni (ustunlar + qatorlar) ajratib oladi.
 * Tushunarli natija bo'lmasa bo'sh massiv qaytaradi.
 */
export function parseSetupSql(sql: string | null | undefined): ParsedTable[] {
  if (!sql || !sql.trim()) return [];

  const tables = new Map<string, ParsedTable>();

  // 1) CREATE TABLE — ustunlar
  CREATE_TABLE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CREATE_TABLE_RE.exec(sql)) !== null) {
    const name = m[1];
    const openIndex = m.index + m[0].length - 1;
    const closeIndex = findClosingParen(sql, openIndex);
    if (closeIndex === -1) continue;

    const body = sql.slice(openIndex + 1, closeIndex);
    const columns = splitTopLevel(body)
      .map(parseColumnDef)
      .filter((c): c is ParsedColumn => c !== null);

    if (columns.length > 0) {
      tables.set(name.toLowerCase(), { name, columns, rows: [] });
    }
  }

  if (tables.size === 0) return [];

  // 2) INSERT INTO ... VALUES — qatorlar
  INSERT_INTO_RE.lastIndex = 0;
  while ((m = INSERT_INTO_RE.exec(sql)) !== null) {
    const table = tables.get(m[1].toLowerCase());
    if (!table) continue;

    // Aniq ustun ro'yxati bo'lsa (INSERT INTO t (a, b) VALUES ...) — o'shanga bog'laymiz
    const explicitCols = m[3]
      ? splitTopLevel(m[3]).map((c) => c.replace(/["`[\]]/g, '').trim())
      : null;

    const valuesStart = m.index + m[0].length;
    const valuesEnd = findStatementEnd(sql, valuesStart);
    const tuples = splitTopLevel(sql.slice(valuesStart, valuesEnd));

    for (const tuple of tuples) {
      const t = tuple.trim();
      if (!t.startsWith('(')) continue;

      const close = findClosingParen(t, 0);
      if (close === -1) continue;

      const values = splitTopLevel(t.slice(1, close)).map(parseValue);
      const targetCols = explicitCols ?? table.columns.map((c) => c.name);

      // Ustun tartibiga moslab qator yig'amiz (aniq ro'yxatda yo'q ustun → null)
      const byName = new Map<string, CellValue>();
      targetCols.forEach((col, i) => byName.set(col.toLowerCase(), values[i] ?? null));

      table.rows.push(table.columns.map((c) => byName.get(c.name.toLowerCase()) ?? null));
    }
  }

  // Faqat ma'lumoti bor jadvallar foydali
  return [...tables.values()].filter((t) => t.rows.length > 0);
}
